import { AQBLOCKS_CORNERCLAMP } from '../consts';
import { Bounds } from '@ardoq/graph';
import { IconName, fontAwesomeIcons } from '@ardoq/icons';
import { BlocksNode } from '../types';
import {
  rectCenter,
  rectFit,
  rectHeight,
  rectSize,
  rectWidth,
} from 'tabview/visualizations/misc/geometry';
import { ArdoqIconCategory, RepresentationData } from '@ardoq/data-model';
import {
  canvasResolveImage,
  canvasResolvedImages,
  svgImage,
} from 'tabview/canvasRendering/canvasResolvedImages';
import { getComposition } from './node.composition';
import { RenderContext } from './types';
import { drawLabel, drawSublabels, drawSvgIcon } from './utils';
import { clamp } from 'lodash';
import { isNodeOpen } from '../util';

const detail_clip = 2; // pixels

const drawRepresentationData = (
  rd: RepresentationData,
  bg: string,
  rcIcon: Bounds,
  params: RenderContext
) => {
  const rc = params.projection.projectBounds(rcIcon);
  const { ctx } = params;

  const isImage = rd.isImage;
  const isFontAwesome =
    rd.icon && rd.icon.category === ArdoqIconCategory.FontAwesome;
  const isSVG = rd.icon && rd.icon.isSVG;
  const isShape = rd.shapeName;
  const ctxGlobalAlpha = ctx.globalAlpha;
  const globalAlpha = ctxGlobalAlpha * params.labelAlpha; // TODO should be rdalpha

  const drawImage = (img: HTMLImageElement) => {
    const imgWidth = img.naturalWidth;
    const imgHeight = img.naturalHeight;
    const outer = rectFit(rc, [imgWidth, imgHeight]);
    const size = rectSize(outer);

    if (size[0] > detail_clip && size[1] > detail_clip) {
      const cornerRadius = clamp(
        0.25 * Math.min(size[0], size[1]),
        AQBLOCKS_CORNERCLAMP[0],
        AQBLOCKS_CORNERCLAMP[1]
      );

      const isClipping = cornerRadius > AQBLOCKS_CORNERCLAMP[0];

      if (isClipping) {
        ctx.save();

        ctx.beginPath();
        ctx.roundRect(
          outer[0],
          outer[1],
          rectWidth(outer),
          rectHeight(outer),
          cornerRadius
        );
        ctx.clip();
      }

      ctx.drawImage(
        img,
        outer[0],
        outer[1],
        rectWidth(outer),
        rectHeight(outer)
      );

      if (isClipping) {
        ctx.restore();
      }
    }
  };

  const drawIcon = () => {
    const diameter = Math.min(rectWidth(rc), rectHeight(rc));
    const outer: Bounds = rectFit(rc, [diameter, diameter]);

    if (rectWidth(outer) > detail_clip && rectHeight(outer) > detail_clip) {
      if (isFontAwesome) {
        const { id } = rd.icon!;
        const fill = params.contrast(bg);
        const size = diameter; // this is used to get the icon font size and should be calculated from inner

        ctx.font = `${size}px FontAwesome`;
        ctx.fillStyle = fill;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.fillText(fontAwesomeIcons[id], ...rectCenter(outer));
      } else {
        const color = params.contrast(bg);
        const fill = params.contrast(bg);
        const stroke = params.contrast(color);
        const negativeColor = 'yellow';
        const key = isSVG ? rd.icon?.id : isShape ? rd.shapeName : undefined;
        const img = key
          ? svgImage(
              `${key}~~${color}~~${fill}~~${stroke}~~100%~~100%~~${negativeColor}`
            )
          : undefined;

        if (img) {
          ctx.drawImage(img, outer[0], outer[1], ...rectSize(outer));
        }
      }
    }
  };

  if (globalAlpha > 0) {
    ctx.globalAlpha = globalAlpha;

    if (isImage) {
      const value = rd.value ?? 'broken-image';

      canvasResolveImage({ isImage: true, value: value });
      const img = canvasResolvedImages.get(value);

      if (img && img.complete && img.naturalWidth && img.naturalHeight) {
        drawImage(img);
      }
    } else {
      drawIcon();
    }

    ctx.globalAlpha = ctxGlobalAlpha;
  }
};

export const drawOpenNode = (
  node: BlocksNode,
  bounds: Bounds,
  color: string,
  fillColor: string,
  params: RenderContext
) => {
  const layout = getComposition(node, bounds);

  if (layout.header) {
    const rc = params.projection.projectBoundsSnapped(layout.header);
    const cornerRadius = params.cornerRadius;

    params.ctx.beginPath();

    if (cornerRadius > AQBLOCKS_CORNERCLAMP[0]) {
      params.ctx.roundRect(rc[0], rc[1], rectWidth(rc), rectHeight(rc), [
        cornerRadius,
        cornerRadius,
        0,
        0,
      ]);
    } else {
      params.ctx.rect(rc[0], rc[1], rectWidth(rc), rectHeight(rc));
    }

    params.ctx.fillStyle = color;
    params.ctx.fill();
  }

  if (layout.expander) {
    drawSvgIcon(
      isNodeOpen(node) ? IconName.UNFOLD_LESS : IconName.UNFOLD_MORE,
      layout.expander,
      color,
      params
    );
  }

  if (layout.rd && node.representationData) {
    drawRepresentationData(node.representationData, color, layout.rd, params);
  }

  const { displayLabels, labelColors } = node;

  if (layout.label && displayLabels.length > 0) {
    const displayLabel = displayLabels[0];
    const labelColor = labelColors.length > 0 ? labelColors[0] : undefined;

    params.ctx.textAlign = 'left';
    drawLabel(displayLabel, labelColor, color, layout.label, params);
  }

  if (layout.sublabels) {
    params.ctx.textAlign = 'left';
    drawSublabels(node, labelColors, fillColor, layout.sublabels, params);
  }
};
